import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { GxButton, GxForm, GxIcon, GxSpinner } from '@garpix/garpix-web-components-react';
import { AsyncPaginate } from 'react-select-async-paginate';
import { useStoreon } from 'storeon/react';
import dayjs from 'dayjs';
import classNames from 'classnames';
import { useFormik } from 'formik';

import { api } from '@/api';
import { ICertificate } from '@/types';
import { validateSchemaActivateCertificate } from '../../schema';
import ModalLayout from '../../../../components/ModalLayout';
import { IFilteredOptions, IModalActivateCertificate } from './interfaces';
import { NAME_TYPES_CERTIFICATE, TYPES_CERTIFICATE } from '../../../Certificates/const';
import { customMultiSelectUserStyles } from '../../consts';
import { CertificateIcon } from '../../../../assets';
import { activateCertificate } from './helpers';
import styles from './styles/index.module.scss';
import FormError from '../../../../components/FormError';

const ModalActivateCertificate: FC<IModalActivateCertificate> = ( props ) => {
	const {
		user,
		flag,
		onRequestClose,
		side
	} = props;

	const [flagSpinner, setFlagSpinner] = useState(false);
	const { dispatch } = useStoreon();
	const toggleFlagSpinner = useCallback(() => setFlagSpinner(( prev ) => !prev), []);
	const [certificates, setCertificates] = useState<ICertificate[]>([]);
	const [inputValue, setInputValue] = useState('')
	const [value, setValue] = useState<Record<string, any> | null>(null)
	
	const formik = useFormik({
		enableReinitialize: true,
		initialValues: {
			number_series: '',
		},
		validationSchema: validateSchemaActivateCertificate,
		validateOnChange: false,
		validateOnBlur: false,
		onSubmit: async ( { number_series } ) => {
			try {
				toggleFlagSpinner();
				const data = await activateCertificate({number_series, user_id: user.id});
				if (data) {
					const arrCert = certificates.map((elem) => {
						if (elem.id === data.id) {
							return {
								...elem,
								status: data.status
							}
						}
						return elem
					})
					setCertificates(arrCert)
				}

				dispatch('certificatesUser/get', {customer_id: user.id, side, page: 1, limit: 7});
			} catch (e) {
				console.warn(e);
			} finally {
				toggleFlagSpinner();
				setValue(null);
				formik.setValues({
					number_series: ''
				});
			}
		}
	});

  
	const loadOptions = async (search: string) => {
    let filteredOptions: IFilteredOptions[] = [];
		if (search) {
			const response = await api.certificate.getUnActivatedCertificate({result_size: 20, search});
			filteredOptions = response.map((el) => {
				return {
					value: el.number,
					label: `${el.number || ''}-${el.series || ''} - ${el.status || ''}`,
          ...el
				}
			})
		}

		return {
			options: filteredOptions
		}
	}

	const selectStyles = {
		...customMultiSelectUserStyles,
		control: (provided: Record<string, any>) => ({
			...provided,
			width: '100%',
			// flexWrap: 0,
			flexFlow: 'initial',
			'@media screen and (max-width:810px)': {
				width: '100%'
			}
		}),
		container: (provided: Record<string, any>) => ({
			...provided,
			width: '100%',
			'@media screen and (max-width:810px)': {
				width: '100%'
			}
		}),
		menu: (provided: Record<string, any>) => ({
			...provided,
			height: '110px'
		}),
		menuList: (provided: Record<string, any>) => ({
			...provided,
			height: '110px'
		}),
	}

	const onChangeHandler = (selectedOption: any) => {
		setValue(selectedOption)
		formik.setFieldValue('number_series', `${selectedOption.number || ''}-${selectedOption.series || ''}`)
	}

	useMemo(() => {
		if (formik.values.number_series) {
			setInputValue('')
		}
	}, [formik.values.number_series])

	const getCertificateData = async (series: string) => {
		const { results } = await api.certificate.getCertificates({series});
		if (results) {
			setCertificates(results)
		}
	}

	useEffect(() => {
		// найти сертик на бэке и вывести в таблицу
		if (value && value.series) {
			getCertificateData(value.series)
		}
	}, [value])

	return (
		<ModalLayout
			flag={flag}
			onClose={onRequestClose}
		>
			<div className={styles[ 'header' ]}>
				<div className={styles[ 'header--container' ]}>
					<GxIcon className={styles[ 'header--icon' ]} src={CertificateIcon}/>
					<h2 className={styles[ 'header--title' ]}>Активация сертификата</h2>
				</div>
				{user && <span>Кому: {user.first_name} {user.last_name}</span>}
			</div>
			<GxForm onGx-submit={formik.submitForm}>
				<div className={styles['main']}>
					<div className={styles.main__select}>
						<p className={styles.main__select__label}>Номер и серия: </p>
						<div className={styles.main__select__inputContainer}>
							<AsyncPaginate
								isClearable
								loadOptions={loadOptions}
								onChange={(e: any) => {
									onChangeHandler(e);
								}}
								onInputChange={(e: any, { action }) => {
									if (
										action === 'menu-close' ||
										action === 'input-blur'
									) {
										return false;
									}
									return setInputValue(e);
								}}
								inputValue={inputValue}
								isOptionDisabled={option => option.status !== NAME_TYPES_CERTIFICATE.VALID.title}
								value={value}
								defaultValue=""
								placeholder="Выберите сертификат"
								debounceTimeout={500}
								styles={selectStyles}
								noOptionsMessage={() => 'Ничего не найдено'}
								loadingMessage={() => 'Поиск...'}
								name="certificates"
							/>
							{formik.errors.number_series &&
								<div className={styles['errors']}>
									<FormError text={formik.errors.number_series}/>
								</div>
							}
						</div>
					</div>
				</div>
				{certificates.length > 0 &&
          <div className={styles[ 'table' ]}>
            <table>
              <thead>
              <tr>
                <th>Название</th>
                <th>Действителен до</th>
                <th>Труба, мин</th>
                <th>Волна, мин</th>
                <th>Статус</th>
              </tr>
              </thead>
              <tbody>
							{certificates.map(( item: Record<string, any>, index: number ) => {
								// eslint-disable-next-line react/no-array-index-key
								return (<tr key={index}>
									<td>{item.title}</td>
									<td>{item.date_validity ? dayjs(item.date_validity).format('YYYY-MM-DD') : '-'}</td>
									<td>{item.session_minutes_fly}</td>
									<td>{item.session_minutes_wave}</td>
									<td>
                  <span
										className={
											classNames(styles[ 'table--status' ],
												styles[ `table--status_${NAME_TYPES_CERTIFICATE[ TYPES_CERTIFICATE[ item?.status ] ]?.class}` ])
										}
									>
                    {NAME_TYPES_CERTIFICATE[ TYPES_CERTIFICATE[ item?.status ] ]?.title}
                  </span>
									</td>
								</tr>)}
							)}
              </tbody>
            </table>
          </div>
				}

				<div className={styles[ 'footer' ]}>
					<div className={styles[ 'footer--container' ]}>
						<GxButton className={styles[ 'btn--close' ]} onClick={onRequestClose}>Отмена</GxButton>
						{flagSpinner ? <GxSpinner style={{ fontSize: '2rem' }}/> :
							<GxButton
							 	className={styles[ 'btn--save' ]} 
								type="submit"
								disabled={!value}
							>
								Активировать
							</GxButton>
						}
					</div>
				</div>
			</GxForm>

		</ModalLayout>
	);
};

export default ModalActivateCertificate;